import React, { useCallback, useEffect, useMemo, useState } from "react";

import { GET_CERAMIC_LOTS_URL, GET_OEM_LOTS_URL, getData } from "../../utils/api";

import "./insights.css";
import "./lot-hedge-settlement.css";

const PAGE_SIZE = 200;
const TABLE_PAGE_SIZE = 50;
const PT_RETURN_PCT = 97.5;
const PD_RETURN_PCT = 97.5;
const RH_RETURN_PCT = 91.5;
let SEARCH_TIMER = null;

const VARIANCE_OPTIONS = [
  { value: "all", suffix: "All" },
  { value: "any", suffix: "Any" },
  { value: "pos", suffix: "+" },
  { value: "neg", suffix: "−" },
  { value: "zero", suffix: "Zero" },
];

function varianceOptions(prefix) {
  return VARIANCE_OPTIONS.map((opt) => ({
    value: opt.value,
    label: `${prefix}: ${opt.suffix}`,
  }));
}

const PT_OZ_VARIANCE_FILTERS = varianceOptions("Pt var");
const PD_OZ_VARIANCE_FILTERS = varianceOptions("Pd var");
const RH_OZ_VARIANCE_FILTERS = varianceOptions("Rh var");

const MATCH_FILTERS = [
  { value: "all", label: "Match: All" },
  { value: "matched", label: "Match: Yes" },
  { value: "unmatched", label: "Match: No" },
];

function matchesVarianceFilter(value, filter) {
  if (!filter || filter === "all") return true;
  // Unmatched lots have no variance — exclude when a variance filter is active
  if (value == null || Number.isNaN(Number(value))) return false;
  const n = Number(value);
  const epsilon = 0.00001;
  switch (filter) {
    case "any":
      return Math.abs(n) >= epsilon;
    case "pos":
      return n > epsilon;
    case "neg":
      return n < -epsilon;
    case "zero":
      return Math.abs(n) < epsilon;
    default:
      return true;
  }
}

function toReturnable(finalOz, pct) {
  if (finalOz == null || Number.isNaN(Number(finalOz))) return null;
  return Number(finalOz) * (pct / 100);
}

function formatOz(n) {
  if (n == null || Number.isNaN(Number(n))) return "—";
  return Number(n).toFixed(2);
}

function formatSignedOz(n) {
  if (n == null || Number.isNaN(Number(n))) return "—";
  const v = Number(n);
  const abs = Math.abs(v).toFixed(2);
  if (Math.abs(v) < 0.00001) return "0.00";
  return v > 0 ? `+${abs}` : `−${abs}`;
}

function varianceClass(n) {
  if (n == null || Number.isNaN(Number(n)) || Math.abs(Number(n)) < 0.00001) return "";
  return Number(n) > 0 ? " is-pos" : " is-neg";
}

function normalizeKey(value) {
  return String(value || "")
    .toLowerCase()
    .replace(/[^a-z0-9]/g, "");
}

async function fetchAllPages(url) {
  const all = [];
  let offset = 0;
  let hasMore = true;
  while (hasMore) {
    const data = await getData(url, { count: PAGE_SIZE, offset });
    const payload = data?.data || data || {};
    const list = payload.list || [];
    all.push(...list);
    hasMore = Boolean(payload.areMoreItemsAvailable) && list.length > 0;
    offset += list.length;
    if (list.length === 0) break;
  }
  return all;
}

const LotHedgeSettlementV3 = () => {
  const [ourLots, setOurLots] = useState([]);
  const [oemLots, setOemLots] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [errorMsg, setErrorMsg] = useState("");
  const [searchInput, setSearchInput] = useState("");
  const [query, setQuery] = useState("");
  const [matchFilter, setMatchFilter] = useState("all"); // all | matched | unmatched
  const [ptOzVarianceFilter, setPtOzVarianceFilter] = useState("all");
  const [pdOzVarianceFilter, setPdOzVarianceFilter] = useState("all");
  const [rhOzVarianceFilter, setRhOzVarianceFilter] = useState("all");
  const [tablePage, setTablePage] = useState(0);

  const load = useCallback(async () => {
    try {
      setIsLoading(true);
      setErrorMsg("");
      const [ours, oem] = await Promise.all([
        fetchAllPages(GET_CERAMIC_LOTS_URL),
        fetchAllPages(GET_OEM_LOTS_URL),
      ]);
      setOurLots(ours);
      setOemLots(oem);
    } catch (e) {
      setOurLots([]);
      setOemLots([]);
      setErrorMsg(e && e.error ? e.error : "Failed to load lots");
    } finally {
      setIsLoading(false);
    }
  }, []);

  useEffect(() => {
    load();
  }, [load]);

  const handleSearchChange = (e) => {
    const value = e.target.value;
    setSearchInput(value);
    if (SEARCH_TIMER) clearTimeout(SEARCH_TIMER);
    SEARCH_TIMER = setTimeout(() => {
      setQuery(value.trim());
      setTablePage(0);
    }, 300);
  };

  // Index oemlots by Number-Lot number for left-join onto our lots
  const oemByNumberLot = useMemo(() => {
    const map = {};
    oemLots.forEach((oem) => {
      const numberLotNumber = String(oem.numberLotNumber || oem.lotName || "").trim();
      const key = normalizeKey(numberLotNumber);
      if (!key) return;
      map[key] = {
        numberLotNumber,
        stillwaterLotNumber: oem.stillwaterLotNumber || oem.oemLotId || "",
        ptOz: Number(oem.ptOz) || 0,
        pdOz: Number(oem.pdOz) || 0,
        rhOz: Number(oem.rhOz) || 0,
      };
    });
    return map;
  }, [oemLots]);

  // 1) Every our lot is a row  2) Enrich from oemlots when Number-Lot number matches
  const rows = useMemo(() => {
    return ourLots
      .map((ours) => {
        const lotName = ours.lotName || "";
        const ourKey = normalizeKey(lotName);
        const oem = ourKey ? oemByNumberLot[ourKey] : null;
        const ourPt = Number(ours.ptOz) || 0;
        const ourPd = Number(ours.pdOz) || 0;
        const ourRh = Number(ours.rhOz) || 0;

        if (oem) {
          const finalPt = oem.ptOz;
          const finalPd = oem.pdOz;
          const finalRh = oem.rhOz;
          const retPt = toReturnable(finalPt, PT_RETURN_PCT);
          const retPd = toReturnable(finalPd, PD_RETURN_PCT);
          const retRh = toReturnable(finalRh, RH_RETURN_PCT);
          return {
            key: `ours-${ourKey}`,
            lotName,
            numberLotNumber: oem.numberLotNumber,
            stillwaterLotNumber: oem.stillwaterLotNumber,
            ourPt,
            ourPd,
            ourRh,
            finalPt,
            finalPd,
            finalRh,
            retPt,
            retPd,
            retRh,
            ptVar: retPt - ourPt,
            pdVar: retPd - ourPd,
            rhVar: retRh - ourRh,
            matched: true,
          };
        }

        return {
          key: `ours-${ourKey || lotName}`,
          lotName,
          numberLotNumber: "",
          stillwaterLotNumber: "",
          ourPt,
          ourPd,
          ourRh,
          finalPt: null,
          finalPd: null,
          finalRh: null,
          retPt: null,
          retPd: null,
          retRh: null,
          ptVar: null,
          pdVar: null,
          rhVar: null,
          matched: false,
        };
      })
      .sort((a, b) => {
        // Matched first, then by lot name
        if (a.matched !== b.matched) return a.matched ? -1 : 1;
        return String(a.lotName).localeCompare(String(b.lotName));
      });
  }, [ourLots, oemByNumberLot]);

  const matchStats = useMemo(() => {
    const matched = rows.filter((r) => r.matched).length;
    const unmatched = rows.length - matched;
    const oemOnly = oemLots.filter((oem) => {
      const key = normalizeKey(oem.numberLotNumber || oem.lotName);
      return key && !ourLots.some((o) => normalizeKey(o.lotName) === key);
    }).length;
    return { matched, unmatched, oemOnly, totalOurs: rows.length, totalOem: oemLots.length };
  }, [rows, oemLots, ourLots]);

  const filteredRows = useMemo(() => {
    const q = query.toLowerCase();
    return rows.filter((r) => {
      if (matchFilter === "matched" && !r.matched) return false;
      if (matchFilter === "unmatched" && r.matched) return false;
      if (!matchesVarianceFilter(r.ptVar, ptOzVarianceFilter)) return false;
      if (!matchesVarianceFilter(r.pdVar, pdOzVarianceFilter)) return false;
      if (!matchesVarianceFilter(r.rhVar, rhOzVarianceFilter)) return false;
      if (q) {
        const hay = `${r.lotName} ${r.numberLotNumber} ${r.stillwaterLotNumber}`.toLowerCase();
        if (!hay.includes(q)) return false;
      }
      return true;
    });
  }, [rows, matchFilter, ptOzVarianceFilter, pdOzVarianceFilter, rhOzVarianceFilter, query]);

  const pageCount = Math.max(1, Math.ceil(filteredRows.length / TABLE_PAGE_SIZE));
  const safePage = Math.min(tablePage, pageCount - 1);
  const pageRows = filteredRows.slice(
    safePage * TABLE_PAGE_SIZE,
    safePage * TABLE_PAGE_SIZE + TABLE_PAGE_SIZE
  );

  const onFilterChange = (setter) => (e) => {
    setter(e.target.value);
    setTablePage(0);
  };

  return (
    <div className="lot-hedge-page">
      <div className="lot-hedge-v3-toolbar">
        <div className="lot-hedge-v3-toolbar-left">
          <span className="lot-hedge-v3-hint">
            Our lots × oemlots on <strong>Number-Lot number</strong> · Ret = Final × 97.5% Pt/Pd,
            91.5% Rh · Var = returnable − ours
          </span>
          <button type="button" className="lot-hedge-link-btn" onClick={load} disabled={isLoading}>
            Refresh
          </button>
        </div>
        <span className="lot-hedge-v2-count">
          {isLoading
            ? "Loading…"
            : `${matchStats.matched} matched / ${matchStats.totalOurs} our lots · ${matchStats.totalOem} oemlots`}
        </span>
      </div>

      {errorMsg ? <div className="lot-hedge-v3-match-warn">{errorMsg}</div> : null}

      <div className="lot-hedge-v2-toolbar">
        <div className="lot-hedge-v2-toolbar-left">
          <input
            type="search"
            className="lot-hedge-v2-search"
            placeholder="Search lot / Stillwater…"
            value={searchInput}
            onChange={handleSearchChange}
            aria-label="Search lot name"
          />
          <select
            className="lot-hedge-v2-filter"
            value={matchFilter}
            onChange={onFilterChange(setMatchFilter)}
            aria-label="Filter by OEM match"
          >
            {MATCH_FILTERS.map((opt) => (
              <option key={opt.value} value={opt.value}>
                {opt.label}
              </option>
            ))}
          </select>
          <select
            className="lot-hedge-v2-filter"
            value={ptOzVarianceFilter}
            onChange={onFilterChange(setPtOzVarianceFilter)}
            aria-label="Filter by Pt ounce variance"
          >
            {PT_OZ_VARIANCE_FILTERS.map((opt) => (
              <option key={opt.value} value={opt.value}>
                {opt.label}
              </option>
            ))}
          </select>
          <select
            className="lot-hedge-v2-filter"
            value={pdOzVarianceFilter}
            onChange={onFilterChange(setPdOzVarianceFilter)}
            aria-label="Filter by Pd ounce variance"
          >
            {PD_OZ_VARIANCE_FILTERS.map((opt) => (
              <option key={opt.value} value={opt.value}>
                {opt.label}
              </option>
            ))}
          </select>
          <select
            className="lot-hedge-v2-filter"
            value={rhOzVarianceFilter}
            onChange={onFilterChange(setRhOzVarianceFilter)}
            aria-label="Filter by Rh ounce variance"
          >
            {RH_OZ_VARIANCE_FILTERS.map((opt) => (
              <option key={opt.value} value={opt.value}>
                {opt.label}
              </option>
            ))}
          </select>
        </div>
        <span className="lot-hedge-v2-count">
          {isLoading ? "Loading…" : `${filteredRows.length} of ${rows.length} lots`}
        </span>
      </div>

      <div className="lot-hedge-table-wrap lot-hedge-v2-table-wrap">
        <table className="lot-hedge-table">
          <thead>
            <tr>
              <th>Our lot (ceramicLot)</th>
              <th>Stillwater lot</th>
              <th>Our Pt</th>
              <th>Our Pd</th>
              <th>Our Rh</th>
              <th>Final Pt</th>
              <th>Final Pd</th>
              <th>Final Rh</th>
              <th>Ret Pt 97.5%</th>
              <th>Ret Pd 97.5%</th>
              <th>Ret Rh 91.5%</th>
              <th>Pt var</th>
              <th>Pd var</th>
              <th>Rh var</th>
            </tr>
          </thead>
          <tbody>
            {isLoading && filteredRows.length === 0 ? (
              <tr>
                <td colSpan={14} className="lot-hedge-v2-empty">
                  Loading our lots + oemlots…
                </td>
              </tr>
            ) : filteredRows.length === 0 ? (
              <tr>
                <td colSpan={14} className="lot-hedge-v2-empty">
                  No rows for this filter.
                </td>
              </tr>
            ) : (
              pageRows.map((row) => (
                <tr key={row.key}>
                  <td>
                    <div className="lot-hedge-lot-id">{row.lotName}</div>
                    {row.matched ? (
                      <div className="lot-hedge-lot-meta">
                        OEM Number-Lot number: {row.numberLotNumber}
                      </div>
                    ) : (
                      <div className="lot-hedge-lot-meta">No oemlots match</div>
                    )}
                  </td>
                  <td>{row.stillwaterLotNumber || "—"}</td>
                  <td>{formatOz(row.ourPt)}</td>
                  <td>{formatOz(row.ourPd)}</td>
                  <td>{formatOz(row.ourRh)}</td>
                  <td>{formatOz(row.finalPt)}</td>
                  <td>{formatOz(row.finalPd)}</td>
                  <td>{formatOz(row.finalRh)}</td>
                  <td>{formatOz(row.retPt)}</td>
                  <td>{formatOz(row.retPd)}</td>
                  <td>{formatOz(row.retRh)}</td>
                  <td className={`lot-hedge-var${varianceClass(row.ptVar)}`}>
                    {formatSignedOz(row.ptVar)}
                  </td>
                  <td className={`lot-hedge-var${varianceClass(row.pdVar)}`}>
                    {formatSignedOz(row.pdVar)}
                  </td>
                  <td className={`lot-hedge-var${varianceClass(row.rhVar)}`}>
                    {formatSignedOz(row.rhVar)}
                  </td>
                </tr>
              ))
            )}
          </tbody>
        </table>
      </div>

      {filteredRows.length > TABLE_PAGE_SIZE && (
        <div className="lot-hedge-v2-more">
          <button
            type="button"
            className="lot-hedge-link-btn"
            disabled={safePage <= 0}
            onClick={() => setTablePage((p) => Math.max(0, p - 1))}
          >
            Prev
          </button>
          <span className="lot-hedge-v2-count">
            Page {safePage + 1} / {pageCount}
          </span>
          <button
            type="button"
            className="lot-hedge-link-btn"
            disabled={safePage >= pageCount - 1}
            onClick={() => setTablePage((p) => Math.min(pageCount - 1, p + 1))}
          >
            Next
          </button>
        </div>
      )}
    </div>
  );
};

export default LotHedgeSettlementV3;
